/** The targeting cursor an adventure spell is aimed with.
 *
 * Three of the map spells take a target the caster picks - Homeward Gate at
 * expert names any town you hold, Blink Step names a cell inside its reach,
 * Sink Vessel names a hull. The kernel has always taken `x/y/z` for all three;
 * until now the shell had nowhere to get them from and passed the cell the hero
 * was already standing on. That cell names no town, is zero tiles away and
 * carries no boat, so Homeward Gate silently collapsed to "the nearest town" and
 * its expert choice of destination existed in the rules and nowhere on screen;
 * Blink Step was refused for distance zero, which greyed it out of the
 * spellbook permanently; and Sink Vessel could only ever name the hull its
 * caster was standing in, which the rule forbids so that nobody drowns.
 *
 * What is in this file is the *state* of an armed cursor and the conversion of
 * the kernel's legal set into something the renderer already knows how to draw.
 * No rule lives here: the cells come from `adventureTargeting`, the refusal of
 * a particular cell comes from `session.check`, and this file decides only what
 * colour the map goes.
 */

import type { HeroId, SpellDefId } from "@game/core";
import { UNREACHABLE_COST } from "@game/core";
import type { ReachableLike } from "@game/render";

export interface Aiming {
  readonly hero: HeroId;
  readonly spell: SpellDefId;
  /** The spell's own name, for the banner. */
  readonly name: string;
  /** `"town"` picks from a list, `"cell"` picks anywhere in a reach. */
  readonly shape: "town" | "cell";
  /** Every legal target, as map cell indices. */
  readonly cells: ReadonlySet<number>;
  /** Chebyshev reach for a `"cell"` spell; 0 for a `"town"` one. */
  readonly range: number;
  /** Where the caster stands, so the map can be pointed at him when armed. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface MapDims {
  readonly width: number;
  readonly height: number;
  readonly levels: number;
}

export const cellIndexOf = (m: MapDims, x: number, y: number, z: number): number =>
  z * m.width * m.height + y * m.width + x;

/**
 * The legal set, in the shape the reachable overlay already takes.
 *
 * Reusing `setReachable` rather than adding a targeting layer to the renderer is
 * deliberate. The wash means "somewhere this hero can be next" in both cases,
 * the layer already clips itself to the fog - which matters, because Dimension
 * Door's legal set includes explored ground the player cannot presently see -
 * and a second overlay would have to answer the same questions about marks,
 * limits and region edges that this one has already answered.
 *
 * `origin` is the caster's own cell so that the selection marker stays under
 * him and his cell is not given a target mark: he is standing on it, and no
 * spell here may be aimed at it.
 */
export function aimingReach(m: MapDims, aim: Aiming): ReachableLike {
  const n = Math.max(1, m.width * m.height * Math.max(1, m.levels));
  const reachable = new Uint8Array(n);
  // Filled with UNREACHABLE_COST, not left at zero. The overlay reads `cost`
  // for every cell whose `reachable` byte is clear and paints anything at or
  // under the budget as "tomorrow's reach" - so a zeroed array washes the
  // WHOLE VISIBLE MAP amber behind the targets, which is what the first
  // screenshot of this overlay showed.
  const cost = new Int32Array(n).fill(UNREACHABLE_COST);
  for (const i of aim.cells) {
    if (i < 0 || i >= n) continue;
    reachable[i] = 1;
    cost[i] = 0;
  }
  return { origin: cellIndexOf(m, aim.x, aim.y, aim.z), cost, reachable };
}

/** Is this cell one the armed spell may be aimed at? */
export const isAimTarget = (m: MapDims, aim: Aiming, x: number, y: number, z: number): boolean =>
  aim.cells.has(cellIndexOf(m, x, y, z));

/**
 * What the banner over the map says while a cursor is armed.
 *
 * It names the spell, what may be clicked, and the way out. A refusal is NOT
 * printed here: the status plate at the foot of the command column is the one
 * place a refusal is spoken (`StatusBar`), and a second copy of it floating
 * over the map would be the same sentence in two voices.
 */
export function aimingPrompt(aim: Aiming): string {
  const what = aim.shape === "town"
    ? "Click a town you hold"
    : `Click a target within ${String(aim.range)} tiles`;
  return `${aim.name}: ${what}. Escape cancels.`;
}
